import { useState, useEffect } from "react";
import {
  Facebook, Instagram, Clock, ThumbsUp, MessageCircle, Share2, MoreHorizontal,
  BadgeCheck, Image as ImageIcon, Heart, Send as SendIcon, Bookmark, AlertCircle,
  Music, Info,
} from "lucide-react";
import { AvatarFallback } from "./_atoms";

export function useMediaAspect(url, kind) {
  const [ratio, setRatio] = useState(1);
  useEffect(() => {
    if (!url) { setRatio(1); return; }
    let cancelled = false;
    if (kind === "video") {
      const v = document.createElement("video");
      v.preload = "metadata";
      v.src = url;
      v.onloadedmetadata = () => {
        if (!cancelled && v.videoWidth && v.videoHeight) {
          setRatio(v.videoWidth / v.videoHeight);
        }
      };
    } else {
      const img = new Image();
      img.src = url;
      img.onload = () => {
        if (!cancelled && img.naturalWidth && img.naturalHeight) {
          setRatio(img.naturalWidth / img.naturalHeight);
        }
      };
    }
    return () => { cancelled = true; };
  }, [url, kind]);
  return ratio;
}

/** Clamp a media aspect ratio to IG feed's allowed range (4:5 ↔ 1.91:1).
 * Meta will crop anything outside this on IG feed posts. */
export function clampIgFeedAspect(r) {
  const min = 4 / 5;      // 0.8 portrait
  const max = 1.91;       // landscape
  return Math.min(Math.max(r, min), max);
}
export function MusicNote() {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-3"
         data-testid="publish-music-note">
      <span className="h-8 w-8 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
        <Music className="h-4 w-4 text-amber-700" />
      </span>
      <div className="text-xs text-amber-900">
        <div className="font-bold flex items-center gap-1"><Info className="h-3 w-3" /> Musica Instagram non disponibile via API</div>
        <div className="mt-1">
          Meta non permette di aggiungere brani della libreria Instagram tramite API pubblica. Puoi:
          <ul className="list-disc pl-5 mt-1 space-y-0.5">
            <li>Includere l'audio direttamente nel video prima di caricarlo</li>
            <li>Oppure aggiungere la traccia dall'app Instagram dopo la pubblicazione</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

// ==========================================================================
// Live previews — FB + IG
// ==========================================================================
export function FbPreview({ profile, text, media, scheduledAt }) {
  const pageName = profile?.page_name || "La tua pagina";
  const pic = profile?.page_picture_url;
  const verified = profile?.verified;
  const mediaCount = media?.length || 0;
  const [activeIdx, setActiveIdx] = useState(0);
  useEffect(() => { setActiveIdx(0); }, [mediaCount]);
  const active = media?.[activeIdx];
  const naturalAspect = useMediaAspect(active?.url, active?.kind);

  return (
    <div className="rounded-xl bg-white overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.1),0_2px_8px_rgba(0,0,0,0.06)]" data-testid="publish-preview-fb"
         style={{ fontFamily: '"SF Pro Text","Segoe UI","Helvetica Neue",Arial,sans-serif' }}>
      <div className="px-3 py-1 bg-[#f0f2f5] border-b border-neutral-200 flex items-center gap-2">
        <Facebook className="h-3.5 w-3.5 text-[#1877F2]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Anteprima Facebook</span>
      </div>
      <div className="p-3">
        {/* Header — page pic + name + timestamp + globe */}
        <div className="flex items-start gap-2 mb-2">
          {pic ? <img src={pic} alt="" className="h-10 w-10 rounded-full object-cover" /> : <AvatarFallback letter={pageName[0]?.toUpperCase() || "?"} className="h-10 w-10 text-sm" />}
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-semibold text-[#050505] leading-tight flex items-center gap-1">
              <span className="truncate">{pageName}</span>
              {verified && <BadgeCheck className="h-3.5 w-3.5 text-[#1877F2] fill-[#1877F2] stroke-white flex-shrink-0" />}
            </div>
            <div className="text-[13px] text-[#65676B] leading-tight mt-0.5 flex items-center gap-1">
              {scheduledAt ? (
                <>Programmato <span className="opacity-60">·</span> <Clock className="h-3 w-3" /> {new Date(scheduledAt).toLocaleString("it-IT", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</>
              ) : (
                <>Adesso <span className="opacity-60">·</span> <svg width="12" height="12" viewBox="0 0 16 16" fill="#65676B"><path d="M8 0a8 8 0 100 16A8 8 0 008 0zM3.5 8a4.5 4.5 0 018-2.75V6a1 1 0 001 1h.5A4.5 4.5 0 013.5 8zm4.5 4.5a4.5 4.5 0 01-4.24-3H4a1 1 0 001-1v-.5a.5.5 0 01.5-.5H7v.5a1 1 0 001 1h.5a1 1 0 011 1v2a4.5 4.5 0 01-.5.05z"/></svg></>
              )}
            </div>
          </div>
          <MoreHorizontal className="h-5 w-5 text-[#65676B]" />
        </div>
        {text && <div className="text-[15px] text-[#050505] whitespace-pre-wrap mb-2 leading-snug">{text}</div>}
        {mediaCount > 0 && (
          <div className="overflow-hidden bg-black -mx-3 relative" style={{ aspectRatio: naturalAspect }}>
            {media[activeIdx]?.kind === "video" ? (
              <video src={media[activeIdx].url} controls className="w-full h-full object-contain" />
            ) : (
              <img src={media[activeIdx]?.url} alt="" className="w-full h-full object-contain" />
            )}
            {mediaCount > 1 && (
              <>
                <div className="absolute top-2 right-2 bg-black/60 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
                  {activeIdx + 1}/{mediaCount}
                </div>
                <button onClick={() => setActiveIdx((i) => Math.max(0, i - 1))} disabled={activeIdx === 0}
                  className="absolute left-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/95 disabled:opacity-30 flex items-center justify-center shadow-md text-[#050505]">‹</button>
                <button onClick={() => setActiveIdx((i) => Math.min(mediaCount - 1, i + 1))} disabled={activeIdx === mediaCount - 1}
                  className="absolute right-2 top-1/2 -translate-y-1/2 h-8 w-8 rounded-full bg-white/95 disabled:opacity-30 flex items-center justify-center shadow-md text-[#050505]">›</button>
              </>
            )}
          </div>
        )}
        {/* Reactions counter (mock preview data) */}
        <div className="mt-2 flex items-center justify-between text-[13px] text-[#65676B] pb-2 border-b border-neutral-200">
          <div className="flex items-center gap-1">
            <span className="inline-flex -space-x-1">
              <span className="h-4 w-4 rounded-full bg-[#1877F2] flex items-center justify-center text-white text-[9px]">👍</span>
              <span className="h-4 w-4 rounded-full bg-[#F33E58] flex items-center justify-center text-white text-[9px]">❤</span>
            </span>
            <span className="ml-1">&nbsp;</span>
          </div>
          <div className="text-[13px]">&nbsp;</div>
        </div>
        {/* Action bar — real FB style */}
        <div className="mt-1 flex items-center justify-around text-[15px] text-[#65676B] font-semibold">
          <button className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded hover:bg-neutral-100">
            <ThumbsUp className="h-[18px] w-[18px]" /> <span>Mi piace</span>
          </button>
          <button className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded hover:bg-neutral-100">
            <MessageCircle className="h-[18px] w-[18px]" /> <span>Commenta</span>
          </button>
          <button className="flex-1 inline-flex items-center justify-center gap-1.5 py-1.5 rounded hover:bg-neutral-100">
            <Share2 className="h-[18px] w-[18px]" /> <span>Condividi</span>
          </button>
        </div>
      </div>
    </div>
  );
}
export function IgPreview({ profile, caption, media, mediaType }) {
  const username = profile?.username || "il_tuo_account";
  const pic = profile?.profile_picture_url;
  const verified = profile?.verified;
  const mediaCount = media?.length || 0;
  const [activeIdx, setActiveIdx] = useState(0);
  const [showFullCaption, setShowFullCaption] = useState(false);
  useEffect(() => { setActiveIdx(0); }, [mediaCount]);
  const active = media?.[activeIdx];
  const naturalAspect = useMediaAspect(active?.url, active?.kind);
  let previewAspect = 1;
  if (mediaType === "STORIES" || mediaType === "REELS") previewAspect = 9 / 16;
  else if (mediaType === "CAROUSEL") previewAspect = 1;
  else if (active) previewAspect = clampIgFeedAspect(naturalAspect);
  const willBeCropped = mediaCount > 0 && (mediaType === "IMAGE" || mediaType === "VIDEO")
    && Math.abs(naturalAspect - previewAspect) > 0.02;
  const isReelOrStory = mediaType === "REELS" || mediaType === "STORIES";
  const captionOver = (caption || "").length > 90;
  const displayedCaption = !captionOver || showFullCaption ? caption : caption.slice(0, 90) + "…";

  return (
    <div className="rounded-xl bg-white overflow-hidden border border-neutral-200"
         data-testid="publish-preview-ig"
         style={{ fontFamily: '"SF Pro Text","-apple-system","Segoe UI",sans-serif' }}>
      <div className="px-3 py-1 bg-neutral-50 border-b border-neutral-200 flex items-center gap-2">
        <Instagram className="h-3.5 w-3.5 text-[#E4405F]" />
        <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Anteprima Instagram · {mediaType}</span>
      </div>
      {/* Header */}
      <div className="px-3 py-2 flex items-center gap-2.5">
        {pic ? (
          <div className="p-[2px] rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
            <img src={pic} alt="" className="h-9 w-9 rounded-full object-cover ring-2 ring-white" />
          </div>
        ) : (
          <div className="p-[2px] rounded-full bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600">
            <div className="h-9 w-9 rounded-full ring-2 ring-white">
              <AvatarFallback letter={username[0]?.toUpperCase() || "?"} className="h-9 w-9 text-sm" />
            </div>
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-black leading-tight flex items-center gap-1">
            <span className="truncate">{username}</span>
            {verified && <BadgeCheck className="h-3.5 w-3.5 text-[#0095F6] fill-[#0095F6] stroke-white flex-shrink-0" />}
          </div>
          <div className="text-[11px] text-neutral-500 leading-tight">Adesso</div>
        </div>
        <MoreHorizontal className="h-5 w-5 text-black" />
      </div>
      {/* Media — edge to edge */}
      <div className="bg-black relative" style={{ aspectRatio: previewAspect }} data-testid="publish-ig-preview-media">
        {mediaCount === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-neutral-500 text-xs gap-2">
            <ImageIcon className="h-8 w-8" />
            {mediaType === "STORIES" ? "Carica una foto o video per la Story"
             : mediaType === "REELS" ? "Carica un video 9:16 per il Reel"
             : "Carica un media"}
          </div>
        ) : media[activeIdx]?.kind === "video" ? (
          <video src={media[activeIdx].url} controls autoPlay={isReelOrStory} muted={isReelOrStory} loop={isReelOrStory} className="w-full h-full object-cover" />
        ) : (
          <img src={media[activeIdx]?.url} alt="" className="w-full h-full object-cover" />
        )}
        {mediaCount > 1 && (
          <>
            <div className="absolute top-3 right-3 bg-black/60 text-white text-[11px] font-semibold px-2 py-0.5 rounded-full">
              {activeIdx + 1}/{mediaCount}
            </div>
            <button onClick={() => setActiveIdx((i) => Math.max(0, i - 1))} disabled={activeIdx === 0}
              className="absolute left-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/90 disabled:opacity-30 flex items-center justify-center shadow">‹</button>
            <button onClick={() => setActiveIdx((i) => Math.min(mediaCount - 1, i + 1))} disabled={activeIdx === mediaCount - 1}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-7 w-7 rounded-full bg-white/90 disabled:opacity-30 flex items-center justify-center shadow">›</button>
          </>
        )}
      </div>
      {/* Footer — IG-style actions + likes + caption */}
      <div className="px-3 pt-2 pb-3">
        {willBeCropped && (
          <div className="mb-2 rounded-lg bg-amber-50 border border-amber-200 p-2 text-[11px] text-amber-900 flex items-start gap-1.5"
               data-testid="publish-ig-crop-warning">
            <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
            <div>
              <span className="font-bold">Il media verrà ritagliato:</span> Instagram accetta il feed tra 4:5 e 1.91:1
              {(mediaType === "IMAGE" || mediaType === "VIDEO") ? " (il tuo file è " + naturalAspect.toFixed(2) + ":1)" : ""}.
              Usa REELS o STORIES per contenuti verticali.
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 mb-1.5">
          <Heart className="h-6 w-6 text-black" strokeWidth={1.8} />
          <MessageCircle className="h-6 w-6 text-black scale-x-[-1]" strokeWidth={1.8} />
          <SendIcon className="h-6 w-6 text-black" strokeWidth={1.8} />
          <Bookmark className="h-6 w-6 text-black ml-auto" strokeWidth={1.8} />
        </div>
        {mediaCount > 1 && (
          <div className="flex justify-center gap-1 mb-1.5">
            {media.map((_, i) => (
              <div key={i} className={`h-1.5 w-1.5 rounded-full ${i === activeIdx ? "bg-[#0095F6]" : "bg-neutral-300"}`} />
            ))}
          </div>
        )}
        {mediaCount > 0 && (
          <div className="text-[13px] font-semibold text-black mb-1">Piace a <span className="font-semibold">amico</span> e <span className="font-semibold">altre persone</span></div>
        )}
        {caption && (
          <div className="text-[13px] text-black leading-snug">
            <span className="font-semibold mr-1.5">{username}</span>
            <span className="whitespace-pre-wrap">{displayedCaption}</span>
            {captionOver && !showFullCaption && (
              <button onClick={() => setShowFullCaption(true)} className="ml-1 text-neutral-500">altro</button>
            )}
          </div>
        )}
        <div className="text-[11px] text-neutral-400 mt-1 uppercase tracking-wide">Adesso</div>
      </div>
    </div>
  );
}

// ==========================================================================
// Compose Toolbar — Emoji + Luogo + Tag utenti (unified popover toolbar)
